import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil, EMPTY, of } from 'rxjs';
import { switchMap, catchError, map } from 'rxjs/operators';
import { PurchaseOrder, PurchaseOrderStatus, PurchaseOrderPriority } from '../../../models/PurchaseOrder';
import {
  PurchaseOrderService,
  PurchaseOrderPagedResult,
  PurchaseOrderListFilters
} from '../../../services/purchase-order.service';
import { AuthService } from '../../../services/auth.service';
import { ErrorService } from '../../../shared/services/error.service';
import { TableAction, TableColumn } from '../../../shared/components/table/table.component';
import { SuppliersService } from '../../../services/suppliers.service';
import { Supplier } from '../../../models/Supplier';
import { StatusService } from '../../../services/status.service';
import { Status } from '../../../models/Status';
import { StaffService } from '../../../services/staff.service';
import { StaffMember } from '../../../models/StaffMember';
import { CostCentersService } from '../../../services/cost-centers.service';
import { CostCenter } from '../../../models/CostCenter';
import { ConfirmationService } from '../../../shared/services/confirmation.service';

@Component({
  selector: 'app-purchase-orders',
  templateUrl: './purchase-orders.component.html',
  styleUrls: ['./purchase-orders.component.css']
})
export class PurchaseOrdersComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private baseRoute = '/purchase-orders';

  // ===== DADOS =====
  private rawPurchaseOrders: PurchaseOrder[] = [];
  purchaseOrders: PurchaseOrder[] = [];
  filteredPurchaseOrders: PurchaseOrder[] = [];
  isLoading = false;
  error: string | null = null;

  // Paginação (alinhado ao backend ?page=&pageSize=)
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  hasNextPage = false;
  hasPreviousPage = false;

  // ===== FILTROS =====
  filters = {
    status: '',
    priority: '',
    supplierId: null as number | string | null,
    requestedByStaffId: null as number | string | null,
    costCenterId: null as number | string | null,
    requestDateFrom: '',
    requestDateTo: '',
    expectedDeliveryFrom: '',
    expectedDeliveryTo: '',
    totalAmountFrom: null as number | null,
    totalAmountTo: null as number | null,
    requiresApproval: null as boolean | string | null,
    search: ''
  };

  private searchTimeout: any = null;

  // ===== DADOS DE APOIO =====
  suppliers: Supplier[] = [];
  staffMembers: StaffMember[] = [];
  costCenters: CostCenter[] = [];

  // Status
  loadingStatuses = false;
  statusOptions: Status[] = [];
  selectedOrderForApprove: PurchaseOrder | null = null;
  selectedOrderForCancel: PurchaseOrder | null = null;
  showApproveModal = false;
  showCancelModal = false;
  approveComments = '';
  cancelReason = '';



  private static readonly SOLICITACAO_STATUS_NAMES = new Set([
    PurchaseOrderStatus.SOLICITADO,
    PurchaseOrderStatus.EM_APROVACAO,
    PurchaseOrderStatus.APROVADO,
    PurchaseOrderStatus.REJEITADO,
    PurchaseOrderStatus.CANCELADO
  ]);

  get displayedStatusOptions(): Status[] {
    if (!this.isSolicitacaoComprasFlow) return this.statusOptions;
    return this.statusOptions.filter(
      (s) => PurchaseOrdersComponent.SOLICITACAO_STATUS_NAMES.has(s.name as PurchaseOrderStatus)
    );
  }

  priorityOptions = [
    { value: PurchaseOrderPriority.BAIXA, label: 'Baixa' },
    { value: PurchaseOrderPriority.NORMAL, label: 'Normal' },
    { value: PurchaseOrderPriority.ALTA, label: 'Alta' },
    { value: PurchaseOrderPriority.URGENTE, label: 'Urgente' }
  ];

  // ===== CONFIGURAÇÃO DA TABELA =====
  showSearch = false;
  searchPlaceholder = 'Buscar por número, descrição, fornecedor...';
  clickable = true;
  sortBy = '';
  sortOrder: 'asc' | 'desc' = 'asc';

  /** Listagem completa (módulo Compras / pedidos). */
  private readonly columnsPedidosCompra: TableColumn[] = [
    { titulo: 'Número', label: 'orderNumber' },
    { titulo: 'Data', label: 'requestDate' },
    { titulo: 'Status', label: 'status' },
    { titulo: 'Prioridade', label: 'priority' },
    { titulo: 'Solicitante', label: 'requestedByStaffName' },
    { titulo: 'Fornecedor', label: 'supplierName' },
    { titulo: 'Valor Total', label: 'totalAmount' },
    { titulo: 'Data Entrega', label: 'expectedDeliveryDate' }
  ];

  /** Solicitação de compras: sem preço e sem data de entrega; com status de aprovação do gestor. */
  private readonly columnsSolicitacaoCompra: TableColumn[] = [
    { titulo: 'Número', label: 'orderNumber' },
    { titulo: 'Data', label: 'requestDate' },
    { titulo: 'Status', label: 'status' },
    { titulo: 'Prioridade', label: 'priority' },
    { titulo: 'Solicitante', label: 'requestedByStaffName' },
    { titulo: 'Aprovação do gestor', label: 'gestorAprovacaoTexto' }
  ];

  get displayColumns(): TableColumn[] {
    return this.isSolicitacaoComprasFlow ? this.columnsSolicitacaoCompra : this.columnsPedidosCompra;
  }

  private readonly actionsBase: TableAction[] = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-view'
    },
    {
      label: 'Editar',
      icon: 'fas fa-pen-to-square',
      action: 'edit',
      class: 'btn-edit'
    },
    {
      label: 'Aprovar',
      icon: 'fas fa-circle-check',
      action: 'approve',
      class: 'btn-success'
    },
    {
      label: 'Cancelar',
      icon: 'fas fa-ban',
      action: 'cancel',
      class: 'btn-delete'
    },
    {
      label: 'Excluir',
      icon: 'fas fa-trash',
      action: 'delete',
      class: 'btn-delete'
    }
  ];

  /**
   * Em «Compras» (/purchase-orders) oculta «Aprovar» e «Excluir» (cancelamento manual segue padrão).
   * Em «Solicitação» (/purchase-requests) oculta «Aprovar» e «Cancelar», expondo «Editar» e «Excluir».
   */
  get tableActions(): TableAction[] {
    if (this.isSolicitacaoComprasFlow) {
      return this.actionsBase.filter((a) => a.action !== 'approve' && a.action !== 'cancel');
    }
    return this.actionsBase.filter((a) => a.action !== 'approve' && a.action !== 'delete');
  }

  constructor(
    private purchaseOrderService: PurchaseOrderService,
    private errorService: ErrorService,
    private router: Router,
    private suppliersService: SuppliersService,
    private statusService: StatusService,
    private staffService: StaffService,
    private costCentersService: CostCentersService,
    private confirmationService: ConfirmationService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.baseRoute = this.router.url.startsWith('/purchase-requests')
      ? '/purchase-requests'
      : '/purchase-orders';
    if (this.isSolicitacaoComprasFlow) {
      this.filters.expectedDeliveryFrom = '';
      this.filters.expectedDeliveryTo = '';
      this.filters.totalAmountFrom = null;
      this.filters.totalAmountTo = null;
    }
    this.loadPurchaseOrders();
    this.loadSupportData();
  }

  /** Menu global «Solicitação de Compras» (/purchase-requests); senão módulo «Compras» (/purchase-orders). */
  get isSolicitacaoComprasFlow(): boolean {
    return this.baseRoute === '/purchase-requests';
  }

  get tableTitle(): string {
    return this.isSolicitacaoComprasFlow ? 'Solicitação de Compras' : 'Pedidos de Compra';
  }

  get newOrderButtonText(): string {
    return this.isSolicitacaoComprasFlow ? 'Nova Solicitação de Compra' : 'Novo Pedido de Compra';
  }

  get emptyStateFilteredMessage(): string {
    return this.isSolicitacaoComprasFlow
      ? 'Nenhuma solicitação de compra encontrada com os filtros aplicados'
      : 'Nenhum pedido de compra encontrado com os filtros aplicados';
  }

  get emptyStateNoDataMessage(): string {
    return this.isSolicitacaoComprasFlow
      ? 'Nenhuma solicitação de compra cadastrada'
      : 'Nenhum pedido de compra cadastrado';
  }

  get emptyStateCreateLabel(): string {
    return this.isSolicitacaoComprasFlow ? 'Criar Primeira Solicitação' : 'Criar Primeiro Pedido';
  }

  get approveModalTitle(): string {
    return this.isSolicitacaoComprasFlow ? 'Aprovar Solicitação de Compra' : 'Aprovar Pedido de Compra';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    // Limpar timeout ao destruir componente
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }

  // ===== CARREGAMENTO DE DADOS =====

  /**
   * Carrega todos os pedidos de compra
   */
  /** resetPage só ao aplicar filtros / primeira carga; troca de página mantém currentPage */
  loadPurchaseOrders(resetPage = false): void {
    if (resetPage) {
      this.currentPage = 1;
    }
    this.isLoading = true;
    this.error = null;

    const backendFilters = this.buildBackendFilters();
    const requestedPageSize = this.pageSize;

    if (this.isSolicitacaoComprasFlow) {
      delete backendFilters.costCenterId;

      const hasClientFilters = !!(
        this.filters.status ||
        this.filters.priority ||
        (this.filters.requestedByStaffId !== null && this.filters.requestedByStaffId !== '') ||
        (this.filters.supplierId !== null && this.filters.supplierId !== '') ||
        this.filters.requestDateFrom ||
        this.filters.requestDateTo ||
        (this.filters.requiresApproval !== null && this.filters.requiresApproval !== '' && this.filters.requiresApproval !== undefined) ||
        (this.filters.search && this.filters.search.trim())
      );

      this.resolveCostCenterIdForCurrentUser()
        .pipe(
          takeUntil(this.destroy$),
          switchMap((ccId) => {
            if (!ccId) {
              this.error =
                'Não foi possível identificar o centro de custo do seu usuário (token ou cadastro de colaborador). Solicite ao administrador que inclua costCenterId no JWT ou vincule seu usuário a um centro de custo.';
              this.rawPurchaseOrders = [];
              this.refreshDisplayedPurchaseOrders();
              this.totalItems = 0;
              this.totalPages = 0;
              this.hasNextPage = false;
              this.hasPreviousPage = false;
              this.isLoading = false;
              return EMPTY;
            }
            if (hasClientFilters) {
              return this.purchaseOrderService.getPurchaseOrdersByCostCenterPaginated(ccId, 1, 500);
            }
            return this.purchaseOrderService.getPurchaseOrdersByCostCenterPaginated(
              ccId, this.currentPage, requestedPageSize
            );
          })
        )
        .subscribe({
          next: (result) => {
            if (hasClientFilters) {
              const filtered = this.applyClientSideFiltersForSolicitacao(result.items || []);
              this.applyPurchaseOrdersPageResult(
                { ...result, items: filtered, totalItems: 0, totalPages: 1 },
                requestedPageSize
              );
            } else {
              this.applyPurchaseOrdersPageResult(result, requestedPageSize);
            }
          },
          error: (error) => {
            this.error = 'Erro ao carregar solicitações do seu centro de custo. Tente novamente.';
            this.isLoading = false;
            console.error('❌ Erro ao carregar solicitações por centro de custo:', error);
            this.errorService.handleHttpError(error);
          }
        });
      return;
    }

    this.purchaseOrderService
      .getAllPurchaseOrdersPaginated(this.currentPage, requestedPageSize, backendFilters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => this.applyPurchaseOrdersPageResult(result, requestedPageSize),
        error: (error) => {
          this.error = 'Erro ao carregar pedidos de compra. Tente novamente.';
          this.isLoading = false;
          console.error('❌ Erro ao carregar pedidos:', error);
          this.errorService.handleHttpError(error);
        }
      });
  }

  private buildBackendFilters(): PurchaseOrderListFilters {
    const backendFilters: PurchaseOrderListFilters = {};
    if (this.filters.status) backendFilters.status = this.filters.status;
    if (this.filters.priority) backendFilters.priority = this.filters.priority;
    if (this.filters.supplierId !== null && this.filters.supplierId !== undefined && this.filters.supplierId !== '') {
      const supplierIdValue =
        typeof this.filters.supplierId === 'string' ? parseInt(this.filters.supplierId, 10) : this.filters.supplierId;
      if (!isNaN(supplierIdValue as number)) {
        backendFilters.supplierId = supplierIdValue as number;
      }
    }
    if (
      this.filters.requestedByStaffId !== null &&
      this.filters.requestedByStaffId !== undefined &&
      this.filters.requestedByStaffId !== ''
    ) {
      const staffIdValue =
        typeof this.filters.requestedByStaffId === 'string'
          ? parseInt(this.filters.requestedByStaffId, 10)
          : this.filters.requestedByStaffId;
      if (!isNaN(staffIdValue as number)) {
        backendFilters.requestedByStaffId = staffIdValue as number;
      }
    }
    if (this.filters.costCenterId !== null && this.filters.costCenterId !== undefined && this.filters.costCenterId !== '') {
      const costCenterIdValue =
        typeof this.filters.costCenterId === 'string' ? parseInt(this.filters.costCenterId, 10) : this.filters.costCenterId;
      if (!isNaN(costCenterIdValue as number)) {
        backendFilters.costCenterId = costCenterIdValue as number;
      }
    }
    if (this.filters.requestDateFrom) backendFilters.requestDateFrom = this.filters.requestDateFrom;
    if (this.filters.requestDateTo) backendFilters.requestDateTo = this.filters.requestDateTo;
    if (!this.isSolicitacaoComprasFlow) {
      if (this.filters.expectedDeliveryFrom) backendFilters.expectedDeliveryFrom = this.filters.expectedDeliveryFrom;
      if (this.filters.expectedDeliveryTo) backendFilters.expectedDeliveryTo = this.filters.expectedDeliveryTo;
      if (this.filters.totalAmountFrom !== null && this.filters.totalAmountFrom !== undefined) {
        backendFilters.totalAmountFrom = this.filters.totalAmountFrom;
      }
      if (this.filters.totalAmountTo !== null && this.filters.totalAmountTo !== undefined) {
        backendFilters.totalAmountTo = this.filters.totalAmountTo;
      }
    }
    if (this.filters.requiresApproval !== null && this.filters.requiresApproval !== undefined && this.filters.requiresApproval !== '') {
      const requiresApprovalValue =
        typeof this.filters.requiresApproval === 'string'
          ? this.filters.requiresApproval === 'true'
          : this.filters.requiresApproval;
      backendFilters.requiresApproval = requiresApprovalValue;
    }
    if (this.filters.search && this.filters.search.trim()) {
      backendFilters.search = this.filters.search.trim();
    }
    return backendFilters;
  }

  private applyPurchaseOrdersPageResult(result: PurchaseOrderPagedResult, requestedPageSize: number): void {
    const rows = result.items || [];
    this.rawPurchaseOrders = rows;
    this.refreshDisplayedPurchaseOrders();
    this.totalItems = Number(result.totalItems ?? 0);
    this.currentPage = Number(result.page ?? 1);
    this.pageSize = requestedPageSize;
    if (this.totalItems <= 0) {
      this.totalPages = 0;
      this.hasNextPage = false;
      this.hasPreviousPage = false;
    } else {
      this.totalPages = this.pageSize > 0 ? Math.ceil(this.totalItems / this.pageSize) : 1;
      this.hasNextPage = this.currentPage < this.totalPages;
      this.hasPreviousPage = this.currentPage > 1;
    }
    this.isLoading = false;
  }

  /** JWT/usuário salvo; senão tenta CostCenterId no cadastro do colaborador (sem toast). */
  private resolveCostCenterIdForCurrentUser() {
    const direct = this.authService.getCostCenterIdFromToken();
    if (direct) {
      return of(direct);
    }
    return this.authService.getStaffId().pipe(
      switchMap((sid) => {
        if (!sid) return of(null);
        return this.staffService.getStaffById(sid, { suppressErrorToast: true }).pipe(
          map((staff) => this.extractCostCenterIdFromStaff(staff)),
          catchError(() => of(null))
        );
      })
    );
  }

  private extractCostCenterIdFromStaff(staff: StaffMember | Record<string, unknown>): number | null {
    const raw = (staff as Record<string, unknown>)?.['CostCenterId'] ?? (staff as Record<string, unknown>)?.['costCenterId'];
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  /**
   * Carrega dados de apoio
   */
  loadSupportData(): void {
    // Catálogo paginado (inclui inativos): listagem só traz supplierId do vencedor — precisa resolver o nome.
    this.suppliersService
      .getAllPaged(1, 500)
      .pipe(
        takeUntil(this.destroy$),
        map((r) => r.items || []),
        catchError(() => of([] as Supplier[]))
      )
      .subscribe({
        next: (list) => {
          this.suppliers = list;
          this.refreshDisplayedPurchaseOrders();
        },
        error: (error) => {
          console.error('Erro ao carregar fornecedores:', error);
          this.errorService.handleHttpError(error);
        }
      });
    
    // Carrega funcionários (mesmo padrão da listagem de staff: endpoint paginado /staff/list)
    this.staffService.getStaffPaginated({ page: 1, pageSize: 500 }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.staffMembers = response?.staff || [];
        this.refreshDisplayedPurchaseOrders();
      },
      error: (error) => { 
        console.error('Erro ao carregar funcionários:', error); 
      }
    });
    
    // Carrega centros de custo
    this.costCentersService.getAll().pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        this.costCenters = result.items || [];
        this.refreshDisplayedPurchaseOrders();
      },
      error: (error) => { 
        console.error('Erro ao carregar centros de custo:', error); 
      }
    });
    
    this.loadPurchaseOrderStatuses();
  }

  /**
   * Carrega os status de pedido de compra dinamicamente
   */
  loadPurchaseOrderStatuses(): void {
    this.loadingStatuses = true;
    this.statusService.getByEntity('PurchaseOrder', true)
      .subscribe({
        next: (statuses: Status[]) => {
          this.statusOptions = statuses;
          this.loadingStatuses = false;
        },
        error: (err) => {
          console.error('Erro ao carregar status de pedidos de compra:', err);
          this.loadingStatuses = false;
          // Fallback para status básicos em caso de erro
          this.statusOptions = [
            { id: 1, name: 'Solicitado', description: 'Pedido solicitado', entityType: 'PurchaseOrder', isActive: true },
            { id: 2, name: 'Em Aprovação', description: 'Pedido em aprovação', entityType: 'PurchaseOrder', isActive: true },
            { id: 3, name: 'Aprovado', description: 'Pedido aprovado', entityType: 'PurchaseOrder', isActive: true },
            { id: 4, name: 'Rejeitado', description: 'Pedido rejeitado', entityType: 'PurchaseOrder', isActive: true },
            { id: 5, name: 'Em Compra', description: 'Pedido em processo de compra', entityType: 'PurchaseOrder', isActive: true },
            { id: 6, name: 'Entregue Parcialmente', description: 'Pedido entregue parcialmente', entityType: 'PurchaseOrder', isActive: true },
            { id: 7, name: 'Entregue Totalmente', description: 'Pedido entregue totalmente', entityType: 'PurchaseOrder', isActive: true },
            { id: 8, name: 'Cancelado', description: 'Pedido cancelado', entityType: 'PurchaseOrder', isActive: true }
          ] as Status[];
        }
      });
  }

  /**
   * Recarrega os dados
   */
  retryLoad(): void {
    this.loadPurchaseOrders();
  }

  onPageChange(page: number): void {
    const p = Number(page);
    if (!Number.isFinite(p) || p < 1) return;
    this.currentPage = p;
    this.loadPurchaseOrders(false);
  }

  onPageSizeChange(size: number): void {
    const s = Number(size);
    if (!Number.isFinite(s) || s < 1) return;
    this.pageSize = s;
    this.currentPage = 1;
    this.loadPurchaseOrders(false);
  }

  // ===== FILTROS =====

  applyFilters(): void {
    this.loadPurchaseOrders(true);
  }

  onTableSearch(term: string): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.filters.search = term;
    this.searchTimeout = setTimeout(() => {
      this.loadPurchaseOrders(true);
      this.searchTimeout = null;
    }, 500);
  }

  onSortChange(event: { sortBy: string; sortOrder: 'asc' | 'desc' }): void {
    this.sortBy = event.sortBy;
    this.sortOrder = event.sortOrder;
    const sorted = this.sortData(this.filteredPurchaseOrders, event.sortBy, event.sortOrder);
    this.filteredPurchaseOrders = sorted;
    this.purchaseOrders = sorted;
  }

  exportToCSV(): void {
    const cols = this.displayColumns;
    const header = cols.map(c => `"${c.titulo}"`).join(',');
    const rows = this.filteredPurchaseOrders.map(item =>
      cols.map(c => `"${String((item as any)[c.label] ?? '').replace(/"/g, '""')}"`).join(',')
    );
    const csv = '﻿' + [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.isSolicitacaoComprasFlow ? 'solicitacoes' : 'pedidos'}-compra.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private applyClientSideFiltersForSolicitacao(items: PurchaseOrder[]): PurchaseOrder[] {
    let result = items;

    const { status, priority, requestedByStaffId, supplierId,
      requestDateFrom, requestDateTo, requiresApproval, search } = this.filters;

    if (status) {
      result = result.filter(i => i.status === status);
    }
    if (priority) {
      result = result.filter(i => i.priority === priority);
    }
    if (requestedByStaffId !== null && requestedByStaffId !== '') {
      const sid = typeof requestedByStaffId === 'string' ? parseInt(requestedByStaffId, 10) : requestedByStaffId as number;
      if (!isNaN(sid)) result = result.filter(i => i.requestedByStaffId === sid);
    }
    if (supplierId !== null && supplierId !== '') {
      const sid = typeof supplierId === 'string' ? parseInt(supplierId as string, 10) : supplierId as number;
      if (!isNaN(sid)) result = result.filter(i => i.supplierId === sid);
    }
    if (requestDateFrom) {
      const from = new Date(requestDateFrom);
      from.setHours(0, 0, 0, 0);
      result = result.filter(i => i.requestDate && new Date(i.requestDate as any) >= from);
    }
    if (requestDateTo) {
      const to = new Date(requestDateTo);
      to.setHours(23, 59, 59, 999);
      result = result.filter(i => i.requestDate && new Date(i.requestDate as any) <= to);
    }
    if (requiresApproval !== null && requiresApproval !== undefined && requiresApproval !== '') {
      const req = typeof requiresApproval === 'string' ? requiresApproval === 'true' : requiresApproval as boolean;
      result = result.filter(i => i.requiresApproval === req);
    }
    if (search && search.trim()) {
      const term = search.toLowerCase().trim();
      result = result.filter(i => {
        const fields = [
          i.orderNumber,
          i.description,
          i.status,
          i.priority,
          (i as any).requestedByStaffName,
          (i as any).supplierName,
          (i as any).costCenterName
        ];
        return fields.some(v => v && String(v).toLowerCase().includes(term));
      });
    }

    return result;
  }

  private sortData<T>(data: T[], sortBy: string, sortOrder: 'asc' | 'desc'): T[] {
    if (!sortBy) return data;
    return [...data].sort((a, b) => {
      const va = (a as any)[sortBy] ?? '';
      const vb = (b as any)[sortBy] ?? '';
      const cmp = String(va).localeCompare(String(vb), 'pt-BR', { numeric: true });
      return sortOrder === 'asc' ? cmp : -cmp;
    });
  }

  /**
   * Limpa todos os filtros
   */
  clearFilters(): void {
    this.filters = {
      status: '',
      priority: '',
      supplierId: null,
      requestedByStaffId: null,
      costCenterId: null,
      requestDateFrom: '',
      requestDateTo: '',
      expectedDeliveryFrom: '',
      expectedDeliveryTo: '',
      totalAmountFrom: null,
      totalAmountTo: null,
      requiresApproval: null,
      search: ''
    };
    this.loadPurchaseOrders(true);
  }

  /**
   * Verifica se há filtros ativos
   */
  hasActiveFilters(): boolean {
    const common =
      !!(
        this.filters.status ||
        this.filters.priority ||
        this.filters.supplierId ||
        this.filters.requestedByStaffId ||
        this.filters.requestDateFrom ||
        this.filters.requestDateTo ||
        this.filters.requiresApproval !== null
      );
    if (this.isSolicitacaoComprasFlow) {
      return common;
    }
    return !!(
      common ||
      this.filters.costCenterId ||
      this.filters.expectedDeliveryFrom ||
      this.filters.expectedDeliveryTo ||
      this.filters.totalAmountFrom !== null ||
      this.filters.totalAmountTo !== null
    );
  }

  private refreshDisplayedPurchaseOrders(): void {
    const displayed = this.rawPurchaseOrders
      .map(order => this.mapOrderForListing(order))
      .sort((a, b) => {
        const da = a.requestDate ? new Date(a.requestDate as any).getTime() : 0;
        const db = b.requestDate ? new Date(b.requestDate as any).getTime() : 0;
        if (db !== da) return db - da;
        return (b.id ?? 0) - (a.id ?? 0);
      });
    this.purchaseOrders = displayed;
    this.filteredPurchaseOrders = displayed;
  }

  private mapOrderForListing(order: PurchaseOrder): PurchaseOrder {
    const supplierName = order.supplierName || this.getSupplierNameById(order.supplierId);
    const requestedByStaffName = order.requestedByStaffName || this.getStaffNameById(order.requestedByStaffId);
    const costCenterName = order.costCenterName || this.getCostCenterNameById(order.costCenterId);

    return {
      ...order,
      supplierName: supplierName || '—',
      requestedByStaffName: requestedByStaffName || '—',
      costCenterName: costCenterName || '—',
      gestorAprovacaoTexto: this.buildGestorAprovacaoLabel(order)
    };
  }

  /**
   * Indica se há registro de aprovação (gestor/nível superior) ou se ainda está pendente.
   */
  private buildGestorAprovacaoLabel(order: PurchaseOrder): string {
    const approvals = order.approvals;
    if (Array.isArray(approvals) && approvals.length > 0) {
      const approvedList = approvals.filter(
        (a) =>
          a?.isApproved === true ||
          String(a?.action || '').toLowerCase().includes('approve') ||
          String(a?.status || '').toLowerCase() === 'approved'
      );
      const managerApproval = approvedList.find((a) => {
        const lvl = String(a?.approvalLevel || '').toLowerCase();
        return (
          lvl.includes('manager') ||
          lvl.includes('gestor') ||
          lvl.includes('gerente') ||
          lvl.includes('supervisor')
        );
      });
      const chosen = managerApproval ?? approvedList[approvedList.length - 1];
      if (chosen) {
        const nome = chosen.staffName || chosen.approverStaffName || '';
        return nome ? `Sim — ${nome}` : 'Sim';
      }
      const rejected = approvals.find((a) => a?.isRejected === true);
      if (rejected) {
        return 'Não — rejeitado';
      }
    }

    const st = String(order.status || '').trim();
    if (st === 'Rejeitado' || st === 'Cancelado') {
      return 'Não';
    }
    if (order.approvalDate && (order.approvedByStaffId || order.approvedByStaffName)) {
      return order.approvedByStaffName ? `Sim — ${order.approvedByStaffName}` : 'Sim';
    }
    if (['Aprovado', 'Em Compra', 'Entregue Parcialmente', 'Entregue Totalmente'].includes(st)) {
      return order.approvedByStaffName ? `Sim — ${order.approvedByStaffName}` : 'Sim';
    }
    if (['Solicitado', 'Em Aprovação'].includes(st)) {
      return 'Pendente';
    }
    if (order.approvedByStaffId || order.approvedByStaffName) {
      return order.approvedByStaffName ? `Sim — ${order.approvedByStaffName}` : 'Sim';
    }
    return '—';
  }

  private getSupplierNameById(supplierId?: number): string | undefined {
    if (!supplierId) return undefined;
    const s = this.suppliers.find((x) => x.id === supplierId);
    if (!s) return undefined;
    const cn = (s.companyName || '').trim();
    const tn = (s.tradeName || '').trim();
    if (cn && tn && tn.toLowerCase() !== cn.toLowerCase()) return `${cn} — ${tn}`;
    return cn || tn || undefined;
  }

  private getStaffNameById(staffId?: number): string | undefined {
    if (!staffId) return undefined;
    const staff = this.staffMembers.find(s => s.Id === staffId);
    return staff?.FullName;
  }

  private getCostCenterNameById(costCenterId?: number): string | undefined {
    if (!costCenterId) return undefined;
    return this.costCenters.find(c => c.id === costCenterId)?.name;
  }

  // ===== AÇÕES DA TABELA =====

  /**
   * Manipula cliques nas ações da tabela
   */
  onActionClick(event: { action: string; item: PurchaseOrder }): void {
    const { action, item } = event;

    switch (action) {
      case 'view':
        this.viewOrder(item.id);
        break;
      case 'edit':
        this.editOrder(item.id);
        break;
      case 'approve':
        this.approveOrder(item);
        break;
      case 'cancel':
        this.cancelOrder(item);
        break;
      case 'delete':
        this.deletePurchaseRequest(item);
        break;
      default:
        break;
    }
  }

  /**
   * Excluir solicitação de compra com confirmação (só usado em /purchase-requests).
   */
  deletePurchaseRequest(order: PurchaseOrder): void {
    if (!order?.id) return;
    const numero = order.orderNumber || `#${order.id}`;
    this.confirmationService.confirm({
      title: 'Excluir Solicitação',
      message: `Deseja realmente excluir a solicitação ${numero}? Esta ação não pode ser desfeita.`,
      confirmText: 'Excluir',
      cancelText: 'Voltar',
      confirmButtonClass: 'btn-danger',
      type: 'warning'
    }).pipe(takeUntil(this.destroy$)).subscribe((confirmed) => {
      if (!confirmed) return;
      this.purchaseOrderService.deletePurchaseRequest(order.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.errorService.showSuccessMessage('Solicitação excluída com sucesso');
            this.loadPurchaseOrders();
          },
          error: (err) => this.errorService.handleHttpError(err)
        });
    });
  }

  // ===== NAVEGAÇÃO =====

  /**
   * Navega para criação de novo pedido
   */
  createNewOrder(): void {
    this.router.navigate([`${this.baseRoute}/new`]);
  }

  /**
   * Navega para detalhes do pedido
   */
  viewOrder(orderId: number | undefined): void {
    if (orderId == null || orderId === undefined) return;
    this.router.navigate([this.baseRoute, orderId]);
  }

  /**
   * Navega para edição do pedido
   */
  editOrder(orderId: number): void {
    this.router.navigate([this.baseRoute, orderId, 'edit']);
  }

  /**
   * Aprova pedido
   */
  approveOrder(order: PurchaseOrder): void {
    this.selectedOrderForApprove = order;
    this.approveComments = '';
    this.showApproveModal = true;
  }

  closeApproveModal(): void {
    this.showApproveModal = false;
    this.selectedOrderForApprove = null;
    this.approveComments = '';
  }

  confirmApproveOrder(): void {
    if (!this.selectedOrderForApprove) return;

    const staffId = 1; // TODO: Obter do usuário logado
    const comments = this.approveComments?.trim() || 'Aprovação via lista de pedidos';

    this.purchaseOrderService.approvePurchaseOrder(this.selectedOrderForApprove.id, {
      staffId,
      comments
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.closeApproveModal();
          this.errorService.showInfoMessage('Pedido aprovado com sucesso');
          this.loadPurchaseOrders();
        },
        error: (error) => {
          this.errorService.handleHttpError(error);
        }
      });
  }

  /**
   * Cancela pedido
   */
  cancelOrder(order: PurchaseOrder): void {
    this.selectedOrderForCancel = order;
    this.cancelReason = '';
    this.showCancelModal = true;
  }

  closeCancelModal(): void {
    this.showCancelModal = false;
    this.selectedOrderForCancel = null;
    this.cancelReason = '';
  }

  confirmCancelOrder(): void {
    if (!this.selectedOrderForCancel) return;

    const reason = this.cancelReason?.trim();
    if (!reason) {
      this.errorService.showErrorMessage('Informe o motivo do cancelamento.');
      return;
    }

    this.confirmationService.confirm({
      title: 'Confirmar Cancelamento',
      message: `Deseja realmente cancelar o pedido ${this.selectedOrderForCancel.orderNumber}?`,
      confirmText: 'Cancelar Pedido',
      cancelText: 'Voltar',
      confirmButtonClass: 'btn-danger',
      type: 'warning'
    }).pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
      if (!confirmed || !this.selectedOrderForCancel) return;

      const staffId = 1; // TODO: Obter do usuário logado
      this.purchaseOrderService.cancelPurchaseOrder(this.selectedOrderForCancel.id, {
        staffId,
        cancellationReason: reason
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.closeCancelModal();
          this.errorService.showInfoMessage('Pedido cancelado com sucesso');
          this.loadPurchaseOrders();
        },
        error: (error) => {
          this.errorService.handleHttpError(error);
        }
      });
    });
  }
}
